import "./DealDetails.css";

import { useMemo } from "react";

import {
  Column,
  DatePicker,
  ErrorBox,
  FullPageCard,
  MultiSelect,
  ReadonlyText,
  Row,
  Select,
  TextArea,
  TextInput,
} from "../../../../widget";
import { NextPreviousPageButtons } from "../components";
import { useDealDetails } from "./useDealDetails";

export const DealDetails = (): JSX.Element => {
  const {
    errors,
    formValues,
    dealPipelinesSelectData,
    organisationUsersSelectData,
    dealSourceSelectData,
    handleFieldChange,
  } = useDealDetails();

  const dealTeamSelectData = useMemo(
    () => (organisationUsersSelectData ?? []).filter((user) => user.key !== formValues.dealLeadUuid),
    [formValues.dealLeadUuid, organisationUsersSelectData]
  );
  const dealTeamValues = useMemo(
    () => (formValues.dealTeamUuids ?? []).filter((uuid) => uuid !== formValues.dealLeadUuid),
    [formValues.dealLeadUuid, formValues.dealTeamUuids]
  );

  const handleDealTeamChange = (values: string[]): void => {
    const nextValues = formValues.dealLeadUuid ? [...values, formValues.dealLeadUuid] : values;
    handleFieldChange("dealTeamUuids")(Array.from(new Set(nextValues)));
  };

  return (
    <div className="DealDetailsContainer">
      <FullPageCard corners="rounded" padding="ll">
        <div className="DealDetailsCard">
          <Row spacingV="ml">
            <Column span={12}>
              <h2>Details</h2>
            </Column>
          </Row>
          {errors && errors.length > 0 && (
            <Row spacingV="ml">
              <Column span={10}>
                <ErrorBox text={errors.map((error) => error.message)} />
              </Column>
            </Row>
          )}
          <form className="DealDetailsForm">
            <Row spacingV="l">
              <Column span="3_half">
                <TextInput
                  label="Deal name"
                  name="Deal name"
                  value={formValues.dealName}
                  onChange={handleFieldChange("dealName")}
                />
              </Column>
              <Column span="3_half" offset={1}>
                {dealPipelinesSelectData && dealPipelinesSelectData.length > 1 ? (
                  <Select
                    label="Deal pipeline"
                    placeholder="Select"
                    data={dealPipelinesSelectData ?? []}
                    value={formValues.dealPipelineUuid}
                    onChange={handleFieldChange("dealPipelineUuid")}
                  />
                ) : (
                  <ReadonlyText label="Deal pipeline" value={dealPipelinesSelectData?.[0].value} />
                )}
              </Column>
            </Row>
            <Row spacingV="l">
              <Column span={8} className="DealDetailsDescription">
                <TextArea
                  label="Description"
                  name="Description"
                  value={formValues.description}
                  placeholder="Enter a description..."
                  onChange={handleFieldChange("description")}
                />
              </Column>
            </Row>
            <Row spacingV="l">
              <Column span="3_half">
                <Select
                  label="Deal lead"
                  placeholder="Select"
                  data={organisationUsersSelectData ?? []}
                  value={formValues.dealLeadUuid}
                  onChange={handleFieldChange("dealLeadUuid")}
                />
              </Column>
              <Column span="3_half" offset={1}>
                <MultiSelect
                  label="Deal team"
                  placeholder="Select"
                  data={dealTeamSelectData}
                  values={dealTeamValues}
                  onChange={handleDealTeamChange}
                />
              </Column>
            </Row>
            <Row spacingV="l">
              <Column span="3_half">
                <Select
                  label="Deal source"
                  placeholder="Select"
                  data={dealSourceSelectData}
                  value={formValues.dealSource}
                  onChange={handleFieldChange("dealSource")}
                />
              </Column>
              <Column span="3_half" offset={1}>
                <DatePicker
                  label="Introduction date"
                  value={formValues.introductionDate}
                  onChange={handleFieldChange("introductionDate")}
                />
              </Column>
            </Row>
            <Row spacingV="l">
              <Column span="3_half">
                <DatePicker
                  label="Target close date"
                  value={formValues.targetCloseDate}
                  onChange={handleFieldChange("targetCloseDate")}
                />
              </Column>
              <Column span="3_half" offset={1}>
                <TextInput
                  type="number"
                  label="Estimated fees (£)"
                  name="Estimated fees"
                  value={formValues.estimatedFees}
                  onChange={handleFieldChange("estimatedFees")}
                />
              </Column>
            </Row>
            <Row spacingV="l">
              <Column span="3_half">
                <TextInput
                  type="number"
                  label="Projected DPI"
                  name="Projected DPI"
                  value={formValues.projectedDpi}
                  onChange={handleFieldChange("projectedDpi")}
                />
              </Column>
              <Column span="3_half" offset={1}>
                <TextInput
                  type="number"
                  label="Conversion probability (%)"
                  name="Conversion probability (%)"
                  value={formValues.conversionProbability}
                  onChange={handleFieldChange("conversionProbability")}
                  decimalPlaces={2}
                />
              </Column>
            </Row>
            <Row>
              <Column span={12}>
                <NextPreviousPageButtons />
              </Column>
            </Row>
          </form>
        </div>
      </FullPageCard>
    </div>
  );
};
